import type {
  RecaptchaRequestConfig,
  RequestErrorInfo,
  RequestErrorData,
  SignInBody,
  User,
  SignIn,
  AuthToken,
  ParticipantScore,
  Participant,
  UserHighestScore,
  UserUpdate,
  ParticipantBody,
  Address,
  LeaderboardParams,
  LeaderboardResponse,
  Campaign,
  CampaignUnit,
  Challenge,
  TicketCode,
  Prize,
  TicketCodeResponse,
  CampaignDataTranslations,
  CampaignTranslations
} from './types';

import axios from 'axios';
import Store from '@/redux';

import {
  getAccessToken,
  getRefreshToken,
  getAccessExpiration,
  getRefreshExpiration
} from '@/redux/auth/selectors';

import {
  setCampaignData,
  setCampaignServerTime
} from '@/redux/campaign/actions';

import {
  setChallengeData,
  setChallengesData
} from '@/redux/challenges/actions';

import { reCAPTCHA } from '@/utils/API';
import type { AxiosError } from 'axios';
import { getAuthCode } from '@/services/app/mcd';
import { setAuthData } from '@/redux/auth/actions';
import type { UserState } from '@/redux/user/types';
import { getUserState } from '@/redux/user/selectors';
import { setUserData, setUserPrize } from '@/redux/user/actions';
import { getCampaignId, getCampaignGameId } from '@/redux/campaign/selectors';
import { getEnv, requireEnv } from '@/utils/env';

class APIService {
  private readonly anonymousUrls = ['/auth/token/refresh', '/auth/signin'];
  private readonly requests: Map<string, number> = new Map();

  private readonly axios = axios.create({
    headers: { common: { 'x-api-key': requireEnv('VITE_API_KEY') } },
    baseURL: `${requireEnv('VITE_API_BASE_URL')}/api`,
    withCredentials: true
  });

  public constructor() {
    this.createInterceptors();
  }

  private isAnonymousUrl(url = ''): boolean {
    return (
      this.anonymousUrls.includes(url) ||
      url === '/translations' ||
      url.startsWith('/campaigns/')
    );
  }

  private async updateExpiredToken(
    state = Store.getState(),
    now = Date.now()
  ): Promise<void> {
    // Force premature expiration if token will expire in 5 seconds:
    const accessExpiration = (getAccessExpiration(state) ?? now) - 5e3;
    const refreshExpiration = (getRefreshExpiration(state) ?? now) - 5e3;

    if (refreshExpiration <= now) {
      await this.login(getUserState(state));
    } else if (accessExpiration <= now) {
      await this.refreshToken(getRefreshToken(state));
    }
  }

  private createInterceptors(): void {
    this.axios.interceptors.request.use(async (config) => {
      this.updateRequests(config.url, true);

      if (config?.url && !this.isAnonymousUrl(config.url)) {
        await this.updateExpiredToken();
        const accessToken = getAccessToken(Store.getState());

        if (accessToken && config?.headers) {
          config.headers['Authorization'] = `Bearer ${accessToken}`;
        }
      }

      return config;
    });

    this.axios.interceptors.response.use(
      async (response) => {
        Store.dispatch(setCampaignServerTime(response.headers.date));
        this.updateRequests(response.config.url);
        return response;
      },

      async (error) => {
        this.updateRequests(error.config?.url);
        return Promise.reject(error);
      }
    );
  }

  private updateRequests(url = '', add = false): void {
    let pi = url.indexOf('?');
    pi = pi < 0 ? url.length : pi;
    const baseUrl = url.slice(0, pi);

    const current = this.requests.get(baseUrl) ?? 0;
    this.requests.set(baseUrl, Math.max(current + +add * 2 - 1, 0));
  }

  private startedRequests(...requests: string[]): boolean {
    for (let r = requests.length; r--;) {
      if (this.requests.get(requests[r])) {
        return true;
      }
    }

    return false;
  }

  private async getReCaptchaConfig(): RecaptchaRequestConfig {
    if (!(window as Application).grecaptcha) {
      if (getEnv('VITE_RECAPTCHA_ENTERPRISE_KEY'))
        throw new Error('ReCaptcha not initialized.');

      return;
    }

    try {
      if (!reCAPTCHA) return;
      const { enterprise } = (window as Application).grecaptcha as GreCaptcha;
      const token = await enterprise.execute(reCAPTCHA, { action: 'post' });
      return { headers: { 'x-recaptcha-token': token } };
    } catch {
      throw new Error('ReCaptcha not fetched.');
    }
  }

  public getRequestErrorInfo(axiosError: AxiosError): RequestErrorInfo {
    const { status = undefined, statusText = undefined } =
      axiosError.response ?? {};

    const {
      statusCode = undefined,
      message = undefined,
      error = undefined
    } = (axiosError.response?.data as RequestErrorData) ?? {};

    return {
      message: message || axiosError.message,
      code: statusCode || status,
      error: error || statusText
    };
  }

  /**
   * Auth Endpoints:
   */
  public revokeToken(): void {
    Store.dispatch(
      setAuthData({
        refreshExpiresAt: null,
        accessExpiresAt: null,
        refreshToken: null,
        accessToken: null
      })
    );
  }

  private getAccessExpiration(delay = 0): number {
    return Date.now() + delay * 1e3;
  }

  public async login(user: UserState): Promise<void> {
    const slug = this.getCampaignSlug();

    if (!this.startedRequests(`/campaigns/${slug}`, '/auth/signin')) {
      const casClientId = getEnv('VITE_CAS_CLIENT_ID');
      const casAuthToken = casClientId && (await getAuthCode(casClientId));

      const campaignId = await this.setCampaignData(user);

      const { challenges, ...me } = await this.signIn({
        mcdonaldsId: user.mcdonaldsId,
        firstName: user.firstname,
        lastName: user.lastname,
        deviceId: user.deviceId,
        locale: user.language,
        email: user.email,
        casAuthToken,
        campaignId
      });

      challenges &&
        Store.dispatch(
          setChallengesData({ challenges: challenges as Challenge[] })
        );

      Store.dispatch(setUserData({ ...user, ...me }));
    }
  }

  private async signIn(data: SignInBody): Promise<User> {
    const config = await this.getReCaptchaConfig();

    const { token, me } = (
      await this.axios.post<SignIn>('/auth/signin', data, config)
    ).data;

    const { accessToken, refreshToken, accessExpiresIn, refreshExpiresIn } =
      token;

    const refreshExpiresAt = this.getAccessExpiration(refreshExpiresIn);
    const accessExpiresAt = this.getAccessExpiration(accessExpiresIn);

    Store.dispatch(
      setAuthData({
        refreshExpiresAt,
        accessExpiresAt,
        refreshToken,
        accessToken
      })
    );

    return me as User;
  }

  private async refreshToken(token: string | null): Promise<AuthToken | void> {
    const { data } = await this.axios.post<AuthToken>('/auth/token/refresh', {
      refreshToken: token
    });

    const { accessToken, refreshToken, accessExpiresIn, refreshExpiresIn } =
      data;

    const refreshExpiresAt = this.getAccessExpiration(refreshExpiresIn);
    const accessExpiresAt = this.getAccessExpiration(accessExpiresIn);

    Store.dispatch(
      setAuthData({
        refreshExpiresAt,
        accessExpiresAt,
        refreshToken,
        accessToken
      })
    );

    return data;
  }

  /**
   * User Endpoints:
   */
  public async submitScore(
    id: string,
    data: ParticipantScore
  ): Promise<Participant> {
    const config = await this.getReCaptchaConfig();

    return (
      await this.axios.patch<Participant>(
        `/participants/${id}/score`,
        data,
        config
      )
    ).data;
  }

  public async getUser(): Promise<User> {
    const { challenges, ...user } = (await this.axios.get<User>('/users/me'))
      .data;

    challenges &&
      Store.dispatch(
        setChallengesData({ challenges: challenges as Challenge[] })
      );

    Store.dispatch(setUserData(user as UserState));

    return user;
  }

  public async getHighestScore(): Promise<number> {
    const id = getCampaignGameId(Store.getState());

    return (
      await this.axios.get<UserHighestScore>(
        `/campaign-games/${id}/my-highest-score`
      )
    ).data.score;
  }

  public async updateUser(data: UserUpdate): Promise<User> {
    const config = await this.getReCaptchaConfig();

    const { challenges, ...user } = (
      await this.axios.patch<User>('/users/me', data, config)
    ).data;

    challenges &&
      Store.dispatch(
        setChallengesData({ challenges: challenges as Challenge[] })
      );

    Store.dispatch(setUserData(user as UserState));

    return user;
  }

  public async runLottery(id: string): Promise<Participant> {
    const config = await this.getReCaptchaConfig();

    return (
      await this.axios.patch<Participant>(
        `/participants/${id}/lottery`,
        undefined,
        config
      )
    ).data;
  }

  public async createLotteryParticipant(): Promise<Participant> {
    const config = await this.getReCaptchaConfig();
    const id = getCampaignGameId(Store.getState());

    return (
      await this.axios.post<Participant>(
        '/participants/with-lottery',
        { campaignGameId: id },
        config
      )
    ).data;
  }

  public async createParticipant(data: ParticipantBody): Promise<Participant> {
    if (!this.startedRequests('/participants')) {
      const config = await this.getReCaptchaConfig();

      return (await this.axios.post<Participant>('/participants', data, config))
        .data;
    }

    throw new Error('Already creating a participant.');
  }

  public async updateAddress(id: string, data: Address): Promise<Participant> {
    const config = await this.getReCaptchaConfig();

    return (
      await this.axios.patch<Participant>(
        `/participants/${id}/address`,
        data,
        config
      )
    ).data;
  }

  /**
   * Campaign Endpoints:
   */
  public async getCampaignLeaderboard(
    params: LeaderboardParams
  ): Promise<LeaderboardResponse> {
    const id = getCampaignGameId(Store.getState());

    return (
      await this.axios.get<LeaderboardResponse>(
        `/campaign-games/${id}/leaderboard`,
        { params }
      )
    ).data;
  }

  private getCampaignSlug(country?: string): string {
    return `${requireEnv('VITE_CAMPAIGN_BASE_SLUG')}${(country && `-${country}`) || ''}`;
  }

  private normalizeLocale(locale?: string): string {
    return (locale || requireEnv('VITE_USER_LANGUAGE')).split('-')[0];
  }

  private setCampaignDataTranslations(
    translations: Record<string, string>,
    source: CampaignDataTranslations,
    language: string,
    separator = '_',
    parent = ''
  ): void {
    for (const key in source) {
      const translationKey = (parent && `${parent}${separator}`) + key;
      const value = source[key];

      if (typeof value === 'string') {
        translations[translationKey] = value;
        continue;
      }

      if (!value || typeof value !== 'object') continue;

      const record = value as Record<string, unknown>;
      const localized = record[language];

      if (typeof localized === 'string') {
        translations[translationKey] = localized;
        continue;
      }

      this.setCampaignDataTranslations(
        translations,
        record,
        language,
        separator,
        translationKey
      );
    }
  }

  private normalizeTranslations(
    payload: CampaignTranslations | Record<string, string> | void
  ): Record<string, string> {
    if (!payload) return {};

    if (!Array.isArray(payload) && typeof payload === 'object') {
      return payload;
    }

    if (!Array.isArray(payload)) return {};

    return payload.reduce<Record<string, string>>((translations, item) => {
      if (!item || typeof item !== 'object') return translations;

      if (item.data && typeof item.data === 'object') {
        Object.assign(translations, item.data);

        for (const key in item.data) {
          translations[`${item.key}_${key}`] = item.data[key];
        }
      } else if (item.key && item.value !== undefined) {
        translations[item.key] = item.value;
      }

      return translations;
    }, {});
  }

  private async getCampaignTranslations(
    campaignId: string,
    locale: string
  ): Promise<CampaignTranslations | void> {
    if (this.startedRequests('/translations')) return;

    return (
      await this.axios.get<CampaignTranslations>('/translations', {
        params: { campaignId, locale: this.normalizeLocale(locale) }
      })
    ).data;
  }

  private async getNormalizedCampaignTranslations(
    translation: CampaignDataTranslations,
    campaignId: string,
    language: string
  ): Promise<Record<string, string>> {
    const translations: Record<string, string> = {};

    this.setCampaignDataTranslations(translations, translation, language);

    const campaignTranslations = await this.getCampaignTranslations(
      campaignId,
      language
    );

    Object.assign(
      translations,
      this.normalizeTranslations(campaignTranslations)
    );

    return translations;
  }

  private async setCampaignData(user?: UserState): Promise<string> {
    const slug = this.getCampaignSlug();
    const { translations: translation = {}, ...campaign } =
      await this.getCampaignBySlug(slug);
    const language = this.normalizeLocale(user?.language);
    const translations = await this.getNormalizedCampaignTranslations(
      translation,
      campaign.id,
      language
    );

    Store.dispatch(setCampaignData({ ...campaign, translations }));
    return campaign.id;
  }

  private async getCampaignBySlug(slug: string): Promise<Campaign> {
    return (await this.axios.get<Campaign>(`/campaigns/${slug}`)).data;
  }

  public async getCampaignUnits(): Promise<CampaignUnit[] | undefined> {
    const campaignId = getCampaignId(Store.getState());

    return (
      await this.axios.get<CampaignUnit[]>(
        `campaign-units?campaignId=${campaignId}`
      )
    ).data;
  }

  /**
   * Challenges Endpoints:
   */
  public async getChallenges(): Promise<Challenge[]> {
    const challenges = (await this.axios.get<Challenge[]>('/challenges')).data;
    Store.dispatch(setChallengesData({ challenges }));
    return challenges;
  }

  public async getChallenge(id: string): Promise<Challenge> {
    const challenge = (await this.axios.get<Challenge>(`/challenges/${id}`))
      .data;

    Store.dispatch(setChallengeData(challenge));
    return challenge;
  }

  public async submitChallenge(id: string, token?: string): Promise<Challenge> {
    const config = await this.getReCaptchaConfig();

    return (
      await this.axios.post<Challenge>(
        `/challenges/${id}/achieve`,
        { token },
        config
      )
    ).data;
  }

  /**
   * Ticket Code Endpoints:
   */
  public async checkTicket(code: string): Promise<TicketCode> {
    return (
      await this.axios.post<TicketCode>('/lottery-ticket-codes/check', {
        campaignId: getCampaignId(Store.getState()),
        code
      })
    ).data;
  }

  public async claimTicket(id: string): Promise<Prize> {
    const {
      lotteryPrize: { prize }
    } = (
      await this.axios.post<TicketCodeResponse>(
        `/lottery-ticket-codes/${id}/lottery`
      )
    ).data;

    Store.dispatch(setUserPrize(prize));
    return prize;
  }
}

export default new APIService();
